<template>
  <!-- <p class="danger-text">
    props.雇用契約ID（koyouKeiyakuId）:{{ koyouKeiyakuId }}<br>
    props.就業時間ID（workTimeId）:{{ workTimeId }}
  </p> -->

  <div class="syuugyou-basyo" v-if="!hideSyuugyouBasyo">
    <b class="title" :class="{required: isRequiredSyuugyouBasyo}">就業場所</b>
    <select v-model="selectedSyuugyouBasyoWithKoyouKeiyakuOption"
      :disabled="syuugyouBasyoWithKoyouKeiyakuOptions.length <= 0 || disableSyuugyouBasyo" @change="changeSyuugyouBasyo">
      <option v-for="syuugyou_basyo in syuugyouBasyoWithKoyouKeiyakuOptions" :key="syuugyou_basyo.koyou_id"
        :value="syuugyou_basyo"
        :selected="syuugyou_basyo.koyou_id === selectedSyuugyouBasyoWithKoyouKeiyakuOption?.koyou_id">
        {{ syuugyou_basyo.label }}
      </option>
      <option v-if="syuugyouBasyoWithKoyouKeiyakuOptions.length <= 0 && isLoading" :value="null">就業場所を取得中…</option>
      <option v-else-if="syuugyouBasyoWithKoyouKeiyakuOptions.length <= 0 && !isLoading" :value="null">
        選択可能な就業場所がありません
      </option>
    </select>
    <p class="input-error" v-if="errors && 'koyou_keiyaku_id' in errors">
      {{ errors['koyou_keiyaku_id'][0] }}
    </p>
    <p v-if="!targetDate">※申請の対象日に応じた就業場所が表示されます</p>
  </div>

  <div class="work-time" v-if="!hideWorkTime">
    <b class="title" :class="{required: isRequiredWorkTime}">就業時間</b>
    <!-- <p class="danger-text">{{ selectedTimeAndHayabanOsoban?.val }}</p> -->
    <select class="work-time-select" v-model="selectedTimeAndHayabanOsoban" :disabled="workTimeOptions.length <= 0 || disableWorkTime"
      @update:modelValue="changeKoyouKeiyakuTime">
      <option v-for="koyouKeiyakuTime in workTimeOptions" :value="koyouKeiyakuTime"
        :selected="selectedTimeAndHayabanOsoban?.val === koyouKeiyakuTime.val">
        {{ koyouKeiyakuTime.label }}
      </option>
      <option v-if="workTimeOptions.length <= 0 && isLoading" :value="null">就業時間を取得中…</option>
      <option v-else-if="workTimeOptions.length <= 0 && !isLoading" :value="null">
        選択可能な就業時間がありません</option>
    </select>
    <p class="input-error" v-if="errors && 'shinsei_koyou_keiyaku_time' in errors">
      {{ errors['shinsei_koyou_keiyaku_time'][0] }}
    </p>
  </div>
</template>

<script lang="ts">

import { defineComponent, toRefs, watch, Ref, ref, onMounted } from 'vue';
import { useKoyouKeiyakuTimeInit } from '@/composable/koyoukeiyakutime/init';
import { useWrokTimeOptions } from '@/composable/koyoukeiyakutime/select-options';
import { KoyouKeiyakuTimeAndHayabanOsobanOption } from "@/types/KoyouKeiyakuTime";
import { Logger } from '@/utils/Logger';
import { useSyuugyouBasyoInit } from '@/composable/syuubyoubasyo/init';
import { KoyouKeiyakuTimeSelectEmitData } from "@/types/SyuugyouBasyo";
import dayjs from 'dayjs';
import { useForm } from '@/composable/form/form';

export default defineComponent({
  props: {
    errors: {
      type: [],
      default: null
    },
    targetDate: {
      type: Object as () => string | Date | null,
      default: null
    },
    koyouKeiyakuId: {
      // 編集時は申請情報を持つ親コンポーネントから就業時間IDを受け取る
      type: Number as () => number | null,
      default: null
    },
    workTimeId: {
      // 編集時は申請情報を持つ親コンポーネントから就業時間IDを受け取る
      type: Number as () => number | null,
      default: null
    },
    userId: {
      type: Number as () => number | null,
      default: null
    },
    hideSyuugyouBasyo: {
      type: Boolean,
      default: false
    },
    disableSyuugyouBasyo: {
      type: Boolean,
      default: false
    },
    isRequiredSyuugyouBasyo: {
      type: Boolean,
      default: true
    },
    hideWorkTime: {
      type: Boolean,
      default: false
    },
    disableWorkTime: {
      type: Boolean,
      default: false
    },
    isRequiredWorkTime: {
      type: Boolean,
      default: true
    },
  },
  emits: {
    change: (data: KoyouKeiyakuTimeSelectEmitData) => true,
  },
  setup(props, context) {
    // エラー取得
    const { getError } = useForm()

    const { targetDate, koyouKeiyakuId, workTimeId, userId, errors } = toRefs(props);
    const { koyouKeiyakuTimeAndHayabanOsobanOptions, getTimeOptionAndShiftByKoyouKeiykuId, selectedTimeAndHayabanOsoban, isLoading } = useKoyouKeiyakuTimeInit()

    const { getOptionForTimeAndHayabanOsoban } = useWrokTimeOptions()
    const { getOptionByUserIdWithKoyouKeiyakuTimes, selectedSyuugyouBasyoWithKoyouKeiyakuOption, syuugyouBasyoWithKoyouKeiyakuOptions } = useSyuugyouBasyoInit()
    const workTimeOptions: Ref<KoyouKeiyakuTimeAndHayabanOsobanOption[]> = ref([])

    onMounted(async () => {
      Logger.debug('----------------------------\n就業場所／就業時間コンポーネントの初期化開始')
      await getSyugyouBasyoOptionsByDate()
      Logger.debug('就業場所／就業時間コンポーネントの初期化完了\n----------------------------')
    })

    // 親コンポーネントから受け取っている対象日の変更検知
    // 原則このコンポーネントは親での日付変更をトリガーとして処理が動き、ここがその起点となる
    watch(() => targetDate.value, async (date) => {
      if (date) {
        getSyugyouBasyoOptionsByDate()
      } else {
        await clearAll()
        change()
      }
    })

    const clearAll = async () => {
      workTimeOptions.value = []
      selectedSyuugyouBasyoWithKoyouKeiyakuOption.value = null
      selectedTimeAndHayabanOsoban.value = null
      syuugyouBasyoWithKoyouKeiyakuOptions.value = []
      Logger.debug('就業場所と就業時間に関する情報をクリアしました')
    }

    // 日付をもとにした就業場所情報の取得
    // 親コンポーネント（各申請）ごとの申請対象日（props.targetDateで渡ってくる値）が変更された場合に走る
    const getSyugyouBasyoOptionsByDate = async () => {
      if (!targetDate.value) {
        await clearAll()
        change()
        Logger.debug('対象日が指定されていないため就業場所一覧の取得を中止します')
        return
      }

      // その日付において選択可能な就業場所一覧を取得
      await getOptionByUserIdWithKoyouKeiyakuTimes(userId.value, true, false, targetDate.value.toString())
      Logger.debug('取得した就業場所一覧：', { "選択中": koyouKeiyakuId.value, "就業場所一覧": syuugyouBasyoWithKoyouKeiyakuOptions.value })

      if (syuugyouBasyoWithKoyouKeiyakuOptions.value.length > 0) {
        if (koyouKeiyakuId.value) {
          Logger.debug('保持している雇用契約IDで就業場所を決定します', { '雇用契約ID': koyouKeiyakuId.value })
          // 編集モードの場合の場合レスポンスに含まれる値初期値として受け取っているため、一致する就業場所を選択する
          // 一致するものがない場合は就業場所一覧の先頭の要素とする
          selectedSyuugyouBasyoWithKoyouKeiyakuOption.value = syuugyouBasyoWithKoyouKeiyakuOptions.value.find(function (v) {
            if (v.koyou_id === koyouKeiyakuId.value) {
              Logger.debug('保持している雇用契約IDと一致する就業場所を選択しました', { '雇用契約ID': v.koyou_id })
              return true
            }
          }) ?? syuugyouBasyoWithKoyouKeiyakuOptions.value[0]
          await changeSyuugyouBasyo()
          return
        } else {
          // 取得した就業場所の先頭の要素を選択状態にする
          selectedSyuugyouBasyoWithKoyouKeiyakuOption.value = syuugyouBasyoWithKoyouKeiyakuOptions.value[0]
          Logger.debug('就業場所一覧の先頭の要素で就業場所を設定しましました', { '就業場所': selectedSyuugyouBasyoWithKoyouKeiyakuOption.value })
          await changeSyuugyouBasyo()
          return

        }
      } else {
        Logger.debug('選択可能な就業場所（雇用契約）が存在しません')
        await clearAll()
        change()
        return
      }
    }

    // 就業場所の変更時の処理
    const changeSyuugyouBasyo = async () => {
      Logger.debug('就業場所が変更されました', selectedSyuugyouBasyoWithKoyouKeiyakuOption.value)
      if (selectedSyuugyouBasyoWithKoyouKeiyakuOption.value) {
        const date = targetDate.value ? targetDate.value.toString() : null
        await getTimeOptionAndShiftByKoyouKeiykuId(selectedSyuugyouBasyoWithKoyouKeiyakuOption.value.koyou_id, null, date)

        workTimeOptions.value = await getOptionForTimeAndHayabanOsoban(targetDate.value, selectedTimeAndHayabanOsoban, koyouKeiyakuTimeAndHayabanOsobanOptions)
        Logger.debug('取得した就業時間一覧：', { "選択中": koyouKeiyakuId, "就業時間一覧": workTimeOptions.value })

        Logger.debug('propsで受け取った就業時間ID', workTimeId.value )
        selectedTimeAndHayabanOsoban.value = workTimeOptions.value.find(function (v) {
          Logger.debug('雇用契約を取得する基準日', v)
          if (dayjs(targetDate.value).isSame(v.date)) {
            return true
          }
          if (v.val === workTimeId.value) {
            Logger.debug('保持している就業時間IDと一致する就業時間を設定しました', { '就業時間ID': v.val, 'propsで受け取った就業時間ID': workTimeId.value })
            return true
          }
        }) ?? null

        if (!selectedTimeAndHayabanOsoban.value && workTimeOptions.value.length > 0) {
          selectedTimeAndHayabanOsoban.value = workTimeOptions.value[0]
          Logger.debug('就業時間一覧の先頭の要素で就業時間を設定しました', { '就業時間': selectedTimeAndHayabanOsoban.value })
        }
      } else {
        await clearAll()
      }

      // 変更を親コンポーネントへ通知
      change()
    }

    // 就業時間の変更時の処理
    const changeKoyouKeiyakuTime = () => {
      Logger.debug('就業時間が変更されました', selectedTimeAndHayabanOsoban.value)
      // 変更を親コンポーネントへ通知
      change()
    }

    // 就業場所または就業時間の変更を親コンポーネントに通知する
    // いずれの場合も就業場所と就業時間がemitされるのでどのように扱うかは受け取り側で決定すること
    const change = () => {
      Logger.debug('[emit] 就業場所または就業時間が変更されたためemitします', { '就業場所': selectedSyuugyouBasyoWithKoyouKeiyakuOption.value, '就業時間': selectedTimeAndHayabanOsoban.value })
      context.emit('change', {
        targetDate: targetDate.value,
        selectedSyuugyouBasyo: selectedSyuugyouBasyoWithKoyouKeiyakuOption.value,
        selectedKoyouKeiyakuTimeOption: selectedTimeAndHayabanOsoban.value,
        koyouKeiyakuTimeAndHayabanOsobanOptions: koyouKeiyakuTimeAndHayabanOsobanOptions.value
      })
    }


    return {
      selectedTimeAndHayabanOsoban,
      koyouKeiyakuTimeAndHayabanOsobanOptions,
      isLoading,
      workTimeOptions,
      changeKoyouKeiyakuTime,
      changeSyuugyouBasyo,
      selectedSyuugyouBasyoWithKoyouKeiyakuOption,
      syuugyouBasyoWithKoyouKeiyakuOptions,
      getError,
      errors
    }
  }
})

</script>
<style lang="scss" scoped>
@import '@scss/components/shinsei-create.scss';
@import '@scss/components/koyoukeiyaku-time-select.scss';
</style>
